// @ts-nocheck
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Compass, Plus, Save, Trash2, Download, Target, 
  ArrowRight, Command, Camera, PauseCircle, BookOpen 
} from 'lucide-react'
import { toast } from 'sonner'
import { format, formatDistanceToNow } from 'date-fns'

import { CreatorLinks } from './lib/creatorLinks'
import type { Decision, Pull, Signal, HesitationCapture, CurveSnapshot, CueCategory } from './lib/types'
import { CUE_LABELS, CUE_IS_STATUS } from './lib/types'
import { generateSeedAngles } from './lib/seedAngles'
import { buildPatienceCurve, CurvePoint } from './lib/curve'

// ==================== CONSTANTS ====================
const LINKEDIN = 'https://www.linkedin.com/in/mindful-abhay/'
const BUYMECOFFEE = 'https://buymeacoffee.com/abhaypratapsingh'

const CUE_CATEGORIES: CueCategory[] = [
  'visibility', 'client_status', 'peer_comparison', 'positioning',
  'craft_truth', 'energy', 'precedent', 'risk_status'
]

function cn(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    credentials: 'include',
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

// Local fallback (so the instrument is always usable)
const LOCAL_KEY = 'declination_local_v1'

function loadLocal() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}') } catch { return {} }
}
function saveLocal(data: any) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(data))
}

// ==================== DECISION COMPASS (the heart) ====================
function DecisionCompass({ 
  pulls, 
  setPulls, 
  trueNorth, 
  setTrueNorth,
  onDeclinationChange 
}: {
  pulls: Pull[]
  setPulls: (p: Pull[]) => void
  trueNorth: number
  setTrueNorth: (n: number) => void
  onDeclinationChange: (d: number) => void
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [dragging, setDragging] = useState<string | 'north' | null>(null)
  const [selectedPull, setSelectedPull] = useState<string | null>(null)

  // Compute composite bearing from pulls (weighted vector sum)
  const compositeBearing = (() => {
    if (pulls.length === 0) return 0
    let x = 0, y = 0
    for (const p of pulls) {
      const rad = (p.angle - 90) * Math.PI / 180 // 0° at top like a real compass
      const w = (p.strength || 50) / 100
      x += Math.cos(rad) * w
      y += Math.sin(rad) * w
    }
    let angle = Math.atan2(y, x) * 180 / Math.PI + 90
    if (angle < 0) angle += 360
    return angle
  })()

  const declination = Math.min(180, Math.abs(((compositeBearing - trueNorth + 180 + 360) % 360) - 180))

  useEffect(() => {
    onDeclinationChange(Math.round(declination * 10) / 10)
  }, [declination, onDeclinationChange])

  // Pointer math
  function getAngleFromEvent(e: React.PointerEvent | PointerEvent): number {
    const svg = svgRef.current
    if (!svg) return 0
    const rect = svg.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dx = (e.clientX - cx)
    const dy = (e.clientY - cy)
    let ang = Math.atan2(dy, dx) * 180 / Math.PI + 90
    if (ang < 0) ang += 360
    return Math.round(ang)
  }

  function updatePull(id: string, angle: number, strength?: number) {
    setPulls(pulls.map(p => p.id === id 
      ? { ...p, angle: angle % 360, strength: strength !== undefined ? Math.max(12, Math.min(100, strength)) : p.strength } 
      : p))
  }

  function updateNorth(angle: number) {
    setTrueNorth(Math.round(angle % 360))
  }

  // Drag handling
  function onPointerDown(e: React.PointerEvent, id: string | 'north') {
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    setDragging(id)
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return
    const angle = getAngleFromEvent(e)
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dist = Math.hypot(e.clientX - cx, e.clientY - cy)
    const maxR = Math.min(rect.width, rect.height) * 0.38

    if (dragging === 'north') {
      updateNorth(angle)
    } else {
      const pull = pulls.find(p => p.id === dragging)
      if (!pull) return
      const newStrength = Math.round(Math.min(100, Math.max(12, (dist / maxR) * 100)))
      updatePull(dragging, angle, newStrength)
    }
  }

  function onPointerUp() {
    setDragging(null)
  }

  // Add a new pull at current mouse angle (or center default)
  function addPull(category: CueCategory) {
    const angle = pulls.length === 0 ? 140 : (pulls[pulls.length-1].angle + 47) % 360
    const newPull: Pull = {
      id: 'p_' + Date.now().toString(36),
      cueCategory: category,
      cueDetail: '',
      strength: category === 'craft_truth' || category === 'energy' ? 68 : 52,
      angle,
    }
    setPulls([...pulls, newPull])
    setSelectedPull(newPull.id)
  }

  function removePull(id: string) {
    setPulls(pulls.filter(p => p.id !== id))
    if (selectedPull === id) setSelectedPull(null)
  }

  function updateCueDetail(id: string, detail: string) {
    setPulls(pulls.map(p => p.id === id ? { ...p, cueDetail: detail } : p))
  }

  const selected = pulls.find(p => p.id === selectedPull)

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="section-title">Decision Compass</div>
          <div className="text-[11px] text-[#6b7280] mt-0.5">Drag tokens. Drag the gold north marker. The amber arc is declination.</div>
        </div>
        <div className="text-right">
          <div className={cn("text-4xl font-semibold tabular-nums tracking-tighter", declination > 52 ? "declination-high" : "declination-low")}>
            {declination.toFixed(0)}°
          </div>
          <div className="text-[10px] uppercase tracking-[1px] text-[#6b7280]">declination</div>
        </div>
      </div>

      <div className="relative bg-[#0a0c14] rounded-3xl p-6 border border-[#1f2537]">
        <svg 
          ref={svgRef}
          width="100%" 
          height="420" 
          viewBox="0 0 420 420" 
          className="compass-container mx-auto block"
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {/* Outer ring */}
          <circle cx="210" cy="210" r="178" fill="none" stroke="#1f2537" strokeWidth="18" />
          <circle cx="210" cy="210" r="178" fill="none" stroke="#2a3146" strokeWidth="1" />

          {/* Cardinal marks */}
          {[0, 90, 180, 270].map((deg, i) => {
            const rad = (deg - 90) * Math.PI / 180
            const x1 = 210 + Math.cos(rad) * 152
            const y1 = 210 + Math.sin(rad) * 152
            const x2 = 210 + Math.cos(rad) * 170
            const y2 = 210 + Math.sin(rad) * 170
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#3a4258" strokeWidth="2" />
          })}

          {/* Subtle degree ticks */}
          {Array.from({ length: 24 }).map((_, i) => {
            const deg = i * 15
            const rad = (deg - 90) * Math.PI / 180
            const r1 = 158
            const r2 = i % 3 === 0 ? 170 : 163
            return (
              <line 
                key={i} 
                x1={210 + Math.cos(rad) * r1} y1={210 + Math.sin(rad) * r1}
                x2={210 + Math.cos(rad) * r2} y2={210 + Math.sin(rad) * r2}
                stroke="#2a3146" strokeWidth="1" 
              />
            )
          })}

          {/* True North marker (gold, draggable) */}
          <g 
            className="true-north cursor-grab active:cursor-grabbing" 
            onPointerDown={(e) => onPointerDown(e, 'north')}
            style={{ transform: `rotate(${trueNorth}deg)`, transformOrigin: '210px 210px' }}
          >
            <line x1="210" y1="210" x2="210" y2="52" stroke="#c9a227" strokeWidth="3.5" strokeLinecap="round" />
            <polygon points="210,38 201,58 219,58" fill="#c9a227" />
            <text x="210" y="32" textAnchor="middle" fill="#c9a227" fontSize="11" fontWeight="600" letterSpacing="1">N</text>
          </g>

          {/* Pull tokens */}
          {pulls.map((pull, idx) => {
            const rad = (pull.angle - 90) * Math.PI / 180
            const r = 52 + (pull.strength / 100) * 92
            const x = 210 + Math.cos(rad) * r
            const y = 210 + Math.sin(rad) * r
            const isStatus = CUE_IS_STATUS[pull.cueCategory]
            const color = isStatus ? '#c17a3f' : '#4a6359'
            const isSel = selectedPull === pull.id

            return (
              <g key={pull.id}>
                {/* faint origin line */}
                <line x1="210" y1="210" x2={x} y2={y} stroke={color} strokeWidth="1" opacity="0.35" />
                
                <g 
                  className="pull-token" 
                  onPointerDown={(e) => { onPointerDown(e, pull.id); setSelectedPull(pull.id) }}
                  style={{ transform: `translate(0, 0)` }}
                >
                  <circle 
                    cx={x} cy={y} r={isSel ? 15 : 12} 
                    fill={color} 
                    stroke={isSel ? "#e2e8f0" : "#0a0c14"} 
                    strokeWidth={isSel ? 2.5 : 2} 
                  />
                  <text x={x} y={y + 4} textAnchor="middle" fill="#05070f" fontSize="9" fontWeight="700">
                    {Math.round(pull.strength)}
                  </text>
                </g>
              </g>
            )
          })}

          {/* Composite bearing needle (thin, calculated) */}
          <g style={{ transform: `rotate(${compositeBearing}deg)`, transformOrigin: '210px 210px' }}>
            <line x1="210" y1="210" x2="210" y2="78" stroke="#e2e8f0" strokeWidth="1.5" opacity="0.85" />
            <circle cx="210" cy="210" r="5" fill="#0a0c14" stroke="#e2e8f0" strokeWidth="1.5" />
          </g>

          {/* Declination arc (visual proof) */}
          {pulls.length > 0 && (
            <g>
              <path 
                d={describeArc(210, 210, 132, Math.min(trueNorth, compositeBearing), Math.max(trueNorth, compositeBearing))}
                fill="none" 
                stroke={declination > 52 ? "#c17a3f" : "#4a6359"} 
                strokeWidth="7" 
                opacity="0.55" 
                strokeLinecap="round"
              />
            </g>
          )}

          <circle cx="210" cy="210" r="9" fill="#0a0c14" stroke="#2a3146" strokeWidth="2" />
        </svg>

        {/* Cue palette */}
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CUE_CATEGORIES.map(cat => {
            const isStatus = CUE_IS_STATUS[cat]
            return (
              <button
                key={cat}
                onClick={() => addPull(cat)}
                className={cn(
                  "text-left px-3 py-2.5 rounded-xl border text-sm transition-all active:scale-[0.985]",
                  isStatus 
                    ? "border-[#3a2a1f] hover:border-[#c17a3f] hover:bg-[#1a140f]" 
                    : "border-[#2a3a32] hover:border-[#4a6359] hover:bg-[#0f1613]"
                )}
              >
                <div className="text-[11px] text-[#6b7280]">{isStatus ? 'STATUS' : 'TRUTH'}</div>
                <div className="font-medium text-[#e2e8f0] leading-tight mt-px">{CUE_LABELS[cat]}</div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Selected pull editor */}
      <AnimatePresence>
        {selected && (
          <motion.div 
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 card p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#6b7280]">{CUE_LABELS[selected.cueCategory]}</div>
                <div className="text-[11px] mt-1 text-[#6b7280]">The specific cue you felt in the moment</div>
              </div>
              <button onClick={() => removePull(selected.id)} className="text-[#b45353] hover:text-red-400 p-1"><Trash2 size={15}/></button>
            </div>
            <textarea 
              className="textarea w-full mt-3 text-sm" 
              placeholder="e.g. “Client said their new head of brand loves ‘ownable’ work that could go on their site”"
              value={selected.cueDetail}
              onChange={(e) => updateCueDetail(selected.id, e.target.value)}
            />
            <div className="mt-3 flex items-center gap-3 text-xs text-[#6b7280]">
              Strength <span className="font-mono tabular-nums text-[#e2e8f0]">{selected.strength}</span>
              <input 
                type="range" min={12} max={100} step="1"
                value={selected.strength}
                onChange={(e) => updatePull(selected.id, selected.angle, parseInt(e.target.value))}
                className="flex-1 accent-[#c17a3f]"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-2 text-[11px] text-[#6b7280] flex items-center gap-2">
        <Target size={13} /> Gold needle = your private north. Amber tokens = status-weighted pulls. Sage = craft/energy pulls.
      </div>
    </div>
  )
}

// SVG arc helper
function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, startAngle)
  const largeArc = endAngle - startAngle <= 180 ? "0" : "1"
  return [
    "M", start.x, start.y, 
    "A", radius, radius, 0, largeArc, 0, end.x, end.y
  ].join(" ")
}
function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * Math.PI / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

// ==================== PATIENCE CURVE (the artifact) ====================
function PatienceCurve({ 
  decision, 
  signals, 
  onAddSignal 
}: { 
  decision: Decision
  signals: Signal[]
  onAddSignal: (s: Omit<Signal, 'id' | 'decisionId' | 'loggedAt'>) => void 
}) {
  const [showAdd, setShowAdd] = useState(false)
  const [newSignal, setNewSignal] = useState({ kind: 'client' as const, note: '', valence: 1 as -1|0|1, magnitude: 3 })
  const released = !!decision.releasedAt

  const points = buildPatienceCurve(decision.releasedAt, signals, decision.releaseNote)

  const width = 620
  const height = 260
  const pad = { l: 38, r: 16, t: 18, b: 28 }

  const maxT = Math.max(14, ...points.map(p => p.t))
  const scaleX = (t: number) => pad.l + ((t / maxT) * (width - pad.l - pad.r))
  const scaleY = (v: number) => height - pad.b - (v / 100) * (height - pad.t - pad.b)

  function handleSvgClick(e: React.MouseEvent<SVGSVGElement>) {
    if (!released) return
    const svg = e.currentTarget
    const rect = svg.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const t = ((clickX - pad.l) / (width - pad.l - pad.r)) * maxT
    if (t < 0.1) return

    // Quick add at this time with sensible defaults
    const kind = 'client'
    const note = 'Signal at ~' + t.toFixed(1) + ' days'
    onAddSignal({ kind, note, valence: 1, magnitude: 3 })
    toast.success('Signal added. Edit the note if you want.')
  }

  async function submitSignal() {
    if (!newSignal.note.trim()) return
    await onAddSignal(newSignal)
    setNewSignal({ kind: 'client', note: '', valence: 1, magnitude: 3 })
    setShowAdd(false)
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="section-title">Public Patience Curve</div>
          <div className="text-xs text-[#6b7280]">External signals (amber) vs your private conviction trace (sage). Click the curve to drop a pin.</div>
        </div>
        {released && (
          <button onClick={() => setShowAdd(true)} className="btn-ghost text-xs px-3 py-1 flex items-center gap-1">
            <Plus size={14}/> Log signal
          </button>
        )}
      </div>

      {!released && (
        <div className="empty-state p-5 text-sm mb-3">
          Mark this decision as released (sent to client / published) to start the patience clock. 
          The curve shows how long it actually took for signals to arrive versus what you privately believed at the time.
        </div>
      )}

      <div className="bg-[#0a0c14] rounded-3xl p-4 border border-[#1f2537] overflow-hidden">
        <svg 
          width="100%" 
          height={height} 
          viewBox={`0 0 ${width} ${height}`} 
          className="curve-svg cursor-crosshair" 
          onClick={handleSvgClick}
        >
          {/* Grid */}
          {[0, 25, 50, 75, 100].map(v => (
            <line key={v} x1={pad.l} y1={scaleY(v)} x2={width - pad.r} y2={scaleY(v)} stroke="#1f2537" strokeWidth="1" />
          ))}
          {Array.from({ length: Math.floor(maxT) + 1 }).map((_, i) => (
            <line key={i} x1={scaleX(i)} y1={pad.t} x2={scaleX(i)} y2={height - pad.b} stroke="#1f2537" strokeWidth="1" />
          ))}

          {/* Axes */}
          <line x1={pad.l} y1={height - pad.b} x2={width - pad.r} y2={height - pad.b} stroke="#2a3146" strokeWidth="1.5" />
          <line x1={pad.l} y1={pad.t} x2={pad.l} y2={height - pad.b} stroke="#2a3146" strokeWidth="1.5" />

          {/* External curve (status / public) */}
          <polyline 
            points={points.map(p => `${scaleX(p.t)},${scaleY(p.external)}`).join(' ')}
            fill="none" stroke="#c17a3f" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" 
          />
          {/* Internal conviction */}
          <polyline 
            points={points.map(p => `${scaleX(p.t)},${scaleY(p.internal)}`).join(' ')}
            fill="none" stroke="#4a6359" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="2 3"
          />

          {/* Points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={scaleX(p.t)} cy={scaleY(p.external)} r="3.5" fill="#c17a3f" />
              {p.internal > 5 && <circle cx={scaleX(p.t)} cy={scaleY(p.internal)} r="2.5" fill="#4a6359" />}
            </g>
          ))}

          {/* Labels */}
          <text x={pad.l} y={height - 8} fill="#6b7280" fontSize="10">0</text>
          <text x={width - pad.r - 18} y={height - 8} fill="#6b7280" fontSize="10">{Math.floor(maxT)}d</text>
          <text x={8} y={scaleY(95)} fill="#c17a3f" fontSize="10">external</text>
          <text x={8} y={scaleY(72)} fill="#4a6359" fontSize="10">private</text>
        </svg>
      </div>

      {/* Signal list + add form */}
      <div className="mt-3 space-y-2">
        {signals.length === 0 && released && (
          <div className="text-xs text-[#6b7280] pl-1">No signals yet. Click the curve or use “Log signal”.</div>
        )}
        {signals.map(s => (
          <div key={s.id} className="flex gap-3 text-sm border-l-2 pl-3 py-0.5" style={{ borderColor: s.kind === 'internal' ? '#4a6359' : '#c17a3f' }}>
            <div className="text-[#6b7280] tabular-nums w-14 shrink-0">{format(new Date(s.loggedAt), 'MMM d')}</div>
            <div className="flex-1 text-[#e2e8f0]">{s.note}</div>
            <div className="text-[#6b7280] text-xs self-center">{s.kind} · {s.valence > 0 ? '+' : s.valence < 0 ? '–' : '•'}{s.magnitude}</div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{opacity:0, y:4}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="mt-3 card p-4 space-y-3">
            <div className="flex gap-2">
              <select value={newSignal.kind} onChange={e => setNewSignal({...newSignal, kind: e.target.value as any})} className="input text-sm py-2">
                <option value="client">Client</option>
                <option value="public">Public / social</option>
                <option value="internal">Internal note</option>
                <option value="commercial">Commercial result</option>
                <option value="silence">Silence / no response</option>
                <option value="other">Other</option>
              </select>
              <input 
                className="input flex-1 text-sm" 
                placeholder="What arrived? (quote, feeling, re-up, invoice, etc)"
                value={newSignal.note}
                onChange={e => setNewSignal({...newSignal, note: e.target.value})}
                onKeyDown={e => { if (e.key === 'Enter') submitSignal() }}
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-[#6b7280]">Valence</div>
              {([-1,0,1] as const).map(v => (
                <button key={v} onClick={() => setNewSignal({...newSignal, valence: v})}
                  className={cn("px-3 py-1 rounded-full text-xs border", newSignal.valence === v ? "border-[#c17a3f] text-[#e2e8f0]" : "border-[#1f2537]")}>
                  {v > 0 ? 'positive' : v === 0 ? 'neutral' : 'difficult'}
                </button>
              ))}
              <div className="flex-1" />
              <button onClick={() => setShowAdd(false)} className="btn-ghost px-4 py-1.5 text-xs">Cancel</button>
              <button onClick={submitSignal} className="btn-primary px-4 py-1.5 text-xs">Add to curve</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ==================== MAIN APP ====================
export default function Declination() {
  const [route, setRoute] = useState<'compass' | 'library' | 'curve' | 'calibration' | 'hesitations' | 'privacy'>('compass')
  const [userId, setUserId] = useState<string | null>(null)

  // Current compass session (not yet saved)
  const [decisionText, setDecisionText] = useState('')
  const [privateNorth, setPrivateNorth] = useState('')
  const [trustPrior, setTrustPrior] = useState(3)
  const [hesitationNote, setHesitationNote] = useState('')
  const [pulls, setPulls] = useState<Pull[]>([])
  const [trueNorth, setTrueNorth] = useState(0)
  const [currentDeclination, setCurrentDeclination] = useState(0)

  // Saved data
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [signalsById, setSignalsById] = useState<Record<string, Signal[]>>({})
  const [hesitations, setHesitations] = useState<HesitationCapture[]>([])
  const [snapshots, setSnapshots] = useState<any[]>([])
  const [calibration, setCalibration] = useState<any>(null)
  const [similar, setSimilar] = useState<any[]>([])
  const [seeds, setSeeds] = useState<string[]>([])

  const [saving, setSaving] = useState(false)
  const [showCmd, setShowCmd] = useState(false)
  const [localOnly, setLocalOnly] = useState(false)

  const selectedDecision = decisions.find(d => d.id === selectedId) || null
  const currentSignals = selectedId ? (signalsById[selectedId] || []) : []

  // Load identity + data
  useEffect(() => {
    api<{ userId: string }>('/api/me').then(r => {
      setUserId(r.userId)
      setLocalOnly(false)
    }).catch(() => {
      const local = 'local_' + Math.random().toString(36).slice(2, 9)
      setUserId(local)
      setLocalOnly(true)
      const loc = loadLocal()
      if (loc.decisions) setDecisions(loc.decisions)
      if (loc.hesitations) setHesitations(loc.hesitations)
    })

    loadAllData()
  }, [])

  async function loadAllData() {
    try {
      const [decRes, hesRes, snapRes, calRes] = await Promise.all([
        api<{ decisions: Decision[] }>('/api/decisions').catch(() => ({ decisions: [] as Decision[] })),
        api<{ hesitations: HesitationCapture[] }>('/api/hesitations').catch(() => ({ hesitations: [] })),
        api<{ snapshots: any[] }>('/api/snapshots').catch(() => ({ snapshots: [] })),
        api<any>('/api/calibration').catch(() => null),
      ])
      setDecisions(decRes.decisions || [])
      setHesitations(hesRes.hesitations || [])
      setSnapshots(snapRes.snapshots || [])
      if (calRes) setCalibration(calRes)
    } catch {
      const loc = loadLocal()
      if (loc.decisions) setDecisions(loc.decisions)
      if (loc.hesitations) setHesitations(loc.hesitations)
    }
  }

  async function loadSignals(decId: string) {
    try {
      const res = await api<{ signals: Signal[] }>(`/api/decisions/${decId}/signals`)
      setSignalsById(prev => ({ ...prev, [decId]: res.signals }))
    } catch {
      // local fallback already in decisions
    }
  }

  // Keyboard command palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setShowCmd(v => !v)
      }
      if (e.key === 'Escape') setShowCmd(false)
      if (e.key.toLowerCase() === 'n' && document.activeElement?.tagName === 'BODY') {
        setRoute('compass')
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 10)
      }
      if (e.key.toLowerCase() === 'h' && document.activeElement?.tagName === 'BODY') {
        setRoute('hesitations')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Persist local when offline
  useEffect(() => {
    if (localOnly) {
      saveLocal({ decisions, hesitations, savedAt: new Date().toISOString() })
    }
  }, [decisions, hesitations, localOnly])

  // ==================== ACTIONS ====================
  async function saveCurrentBearing() {
    if (!decisionText.trim() || !privateNorth.trim()) {
      toast.error('Write the decision and your private north first.')
      return
    }
    setSaving(true)
    const payload = {
      decisionText: decisionText.trim(),
      privateNorth: privateNorth.trim(),
      trustPrior,
      hesitationNote: hesitationNote.trim() || undefined,
      pulls: pulls.map(p => ({ ...p, cueDetail: p.cueDetail.trim() })),
      declination: currentDeclination,
    }

    try {
      if (localOnly) {
        const fake: Decision = {
          id: 'loc_' + Date.now(),
          userId: userId || 'local',
          createdAt: new Date().toISOString(),
          ...payload,
          pulls: payload.pulls,
        } as any
        setDecisions([fake, ...decisions])
        toast.success('Saved locally (backend unreachable)')
      } else {
        const res = await api<{ decision: Decision }>('/api/decisions', { method: 'POST', body: JSON.stringify(payload) })
        setDecisions([res.decision, ...decisions])
        setSelectedId(res.decision.id)
        setRoute('curve')
        toast.success('Bearing dropped. The compass remembers.')
      }
      // reset composer
      setDecisionText(''); setPrivateNorth(''); setHesitationNote(''); setPulls([]); setTrueNorth(0); setCurrentDeclination(0); setTrustPrior(3)
    } catch (e: any) {
      toast.error('Save failed: ' + (e.message || 'unknown'))
    } finally {
      setSaving(false)
    }
  }

  async function markReleased() {
    if (!selectedId) return
    const note = prompt('Private note at release (optional, only you will see it):') || undefined
    const exp = prompt('Expected external signal strength in first 14 days? (1-5, optional)') || undefined
    try {
      const res = await api<{ decision: Decision }>(`/api/decisions/${selectedId}/release`, {
        method: 'PUT',
        body: JSON.stringify({ releaseNote: note, expectedVelocity: exp ? parseInt(exp) : undefined })
      })
      setDecisions(decs => decs.map(d => d.id === selectedId ? res.decision : d))
      toast.success('Patience clock started.')
      await loadSignals(selectedId)
    } catch {
      // local
      setDecisions(decs => decs.map(d => d.id === selectedId ? { ...d, releasedAt: new Date().toISOString(), releaseNote: note } : d))
    }
  }

  async function addSignalToSelected(s: Omit<Signal, 'id' | 'decisionId' | 'loggedAt'>) {
    if (!selectedId) return
    try {
      const res = await api<{ signals: Signal[] }>(`/api/decisions/${selectedId}/signals`, {
        method: 'POST', body: JSON.stringify(s)
      })
      setSignalsById(prev => ({ ...prev, [selectedId]: res.signals }))
    } catch {
      const fake: Signal = { 
        id: 'locsig_' + Date.now(), decisionId: selectedId, loggedAt: new Date().toISOString(), ...s 
      }
      setSignalsById(prev => ({ ...prev, [selectedId]: [...(prev[selectedId] || []), fake] }))
    }
  }

  async function captureHesitation(text: string, decisionId?: string) {
    if (!text.trim()) return
    try {
      await api('/api/hesitations', { method: 'POST', body: JSON.stringify({ text: text.trim(), decisionId }) })
      await loadAllData()
    } catch {
      const h: HesitationCapture = { id: 'loch_' + Date.now(), capturedAt: new Date().toISOString(), text: text.trim(), decisionId }
      setHesitations([h, ...hesitations])
    }
    toast('Hesitation captured. It counts.')
  }

  async function freezeCurve() {
    if (!selectedDecision) return
    const canvas = document.createElement('canvas')
    canvas.width = 1100
    canvas.height = 620
    const ctx = canvas.getContext('2d', { alpha: true })!
    // Simple beautiful render (we could improve with real SVG raster but this is complete & functional)
    ctx.fillStyle = '#05070f'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#c9a227'
    ctx.font = '600 28px Georgia, serif'
    ctx.fillText('DECLINATION', 60, 70)
    ctx.fillStyle = '#e2e8f0'
    ctx.font = '500 18px Inter, system-ui'
    ctx.fillText(selectedDecision.decisionText.slice(0, 78), 60, 110)
    ctx.fillStyle = '#6b7280'
    ctx.font = '14px Inter, system-ui'
    ctx.fillText(`Declination at release: ${selectedDecision.declination.toFixed(0)}°   •   ${format(new Date(), 'dd MMM yyyy')}`, 60, 145)

    // Draw a minimal curve representation
    const pts = buildPatienceCurve(selectedDecision.releasedAt, currentSignals)
    ctx.strokeStyle = '#c17a3f'
    ctx.lineWidth = 3
    ctx.beginPath()
    pts.forEach((p, i) => {
      const x = 80 + (i / Math.max(1, pts.length - 1)) * 920
      const y = 220 + (1 - p.external / 100) * 240
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    })
    ctx.stroke()

    ctx.strokeStyle = '#4a6359'
    ctx.lineWidth = 2
    ctx.setLineDash([3, 3])
    ctx.beginPath()
    pts.forEach((p, i) => {
      const x = 80 + (i / Math.max(1, pts.length - 1)) * 920
      const y = 220 + (1 - p.internal / 100) * 240
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    })
    ctx.stroke()
    ctx.setLineDash([])

    const pngBase64 = canvas.toDataURL('image/png').split(',')[1]

    try {
      const res = await api<{ snapshot: CurveSnapshot }>(`/api/decisions/${selectedDecision.id}/snapshots`, {
        method: 'POST',
        body: JSON.stringify({
          pngBase64,
          summary: `Declination ${selectedDecision.declination.toFixed(0)}°. ${currentSignals.length} signals logged.`,
          isPublicGallery: false,
        })
      })
      toast.success('Patience curve frozen and stored.')
      setSnapshots(s => [res.snapshot, ...s])
    } catch {
      toast('Frozen locally (R2 unavailable in this session)')
    }
  }

  async function loadSeedsForSelected() {
    if (!selectedId) return
    try {
      const res = await api<{ seeds: string[] }>(`/api/decisions/${selectedId}/seeds`, { method: 'POST' })
      setSeeds(res.seeds)
    } catch {
      const dec = selectedDecision
      if (dec) setSeeds(generateSeedAngles(dec))
    }
  }

  async function viewCalibration() {
    setRoute('calibration')
    const cal = await api<any>('/api/calibration').catch(() => calibration)
    if (cal) setCalibration(cal)
  }

  function selectDecision(d: Decision) {
    setSelectedId(d.id)
    setRoute('curve')
    loadSignals(d.id)
    setSeeds([])
    setSimilar([])
  }

  async function loadSimilar() {
    if (!selectedId) return
    try {
      const res = await api<{ similar: any[] }>(`/api/decisions/${selectedId}/similar`)
      setSimilar(res.similar || [])
    } catch {}
  }

  function quickHesitation() {
    const txt = prompt('What are you hesitating about right now? (the social/status reason)')
    if (txt) captureHesitation(txt, selectedId || undefined)
  }

  async function doExport() {
    try {
      const data = await api('/api/export')
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `declination-export-${format(new Date(), 'yyyy-MM-dd')}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      const blob = new Blob([JSON.stringify({ decisions, hesitations }, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'declination-local-export.json'; a.click(); URL.revokeObjectURL(url)
    }
  }

  async function deleteEverything() {
    if (!confirm('Delete every decision, signal, hesitation, and snapshot forever?')) return
    try { await api('/api/delete-all', { method: 'POST' }) } catch {}
    setDecisions([]); setSignalsById({}); setHesitations([]); setSnapshots([])
    toast('Everything erased from this compass.')
  }

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-[#05070f] text-[#e2e8f0] pb-24">
      {/* Top nav */}
      <div className="border-b border-[#1f2537] sticky top-0 z-50 bg-[#05070f]/95 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div onClick={() => setRoute('compass')} className="flex items-center gap-2 cursor-pointer">
              <div className="w-6 h-6 rounded-full border border-[#c9a227] flex items-center justify-center">
                <Compass size={14} className="text-[#c9a227]" />
              </div>
              <div className="font-semibold tracking-tight text-lg">Declination</div>
            </div>
            <div className="text-[10px] px-2 py-px rounded bg-[#1f2537] text-[#6b7280] tracking-[1px] mt-0.5">FREELANCE • STATUS • PROVENANCE</div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => setRoute('compass')} className={cn("nav-link px-3 py-1", route === 'compass' && 'active')}>New bearing</button>
            <button onClick={() => setRoute('library')} className={cn("nav-link px-3 py-1", route === 'library' && 'active')}>Library</button>
            <button onClick={viewCalibration} className={cn("nav-link px-3 py-1", route === 'calibration' && 'active')}>Calibration</button>
            <button onClick={() => setRoute('hesitations')} className={cn("nav-link px-3 py-1", route === 'hesitations' && 'active')}>Hesitations</button>
            <button onClick={quickHesitation} className="btn-ghost px-3 py-1 text-xs flex items-center gap-1"><PauseCircle size={14}/>Capture hesitation</button>
            <button onClick={() => setShowCmd(true)} className="btn-ghost px-2.5 py-1 text-xs"><Command size={14}/></button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-8">
        {/* Hero / thesis */}
        {route === 'compass' && !selectedId && (
          <div className="mb-8">
            <div className="font-serif text-6xl tracking-[-1.6px] leading-none mb-3">The pull<br />you usually miss.</div>
            <div className="max-w-xl text-xl text-[#a1a7c0]">
              Status anxiety quietly rotates your creative decisions away from the actual work and toward how you imagine you will be seen.
              Declination makes the angle visible at the moment of choice, then shows you the real patience curve of what arrives afterward.
            </div>
            <div className="mt-4 flex items-center gap-3 text-sm">
              <CreatorLinks />
            </div>
          </div>
        )}

        {/* COMPASS — capture */}
        {route === 'compass' && (
          <div className="max-w-3xl">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[2px] text-[#c17a3f] mb-1">THE INSTRUMENT</div>
              <div className="text-2xl font-medium tracking-tight">Log a bearing</div>
              <div className="text-[#6b7280] mt-1">One decision. The exact cues you weighted. How far they sit from what you actually care about.</div>
            </div>

            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-1.5">What decision are you making right now?</div>
                <textarea 
                  className="textarea w-full" 
                  placeholder="e.g. Whether to lead the proposal with a speculative 3-week research phase or go straight to the visual system they asked for"
                  value={decisionText} 
                  onChange={e => setDecisionText(e.target.value)} 
                />
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-1.5">Your private north (what would actually be good for the work / client / your energy, ignoring how it photographs)</div>
                <textarea 
                  className="textarea w-full" 
                  placeholder="The client’s users will be confused by motion. I will have to do three rounds of revisions either way. I want to sleep at night."
                  value={privateNorth} 
                  onChange={e => setPrivateNorth(e.target.value)} 
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-3">
                  <DecisionCompass 
                    pulls={pulls} 
                    setPulls={setPulls} 
                    trueNorth={trueNorth} 
                    setTrueNorth={setTrueNorth} 
                    onDeclinationChange={setCurrentDeclination} 
                  />
                </div>
                <div className="lg:col-span-2 space-y-4">
                  <div className="card p-4">
                    <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-2">Trust prior right now</div>
                    <div className="flex items-center gap-3">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} onClick={() => setTrustPrior(n)} className={cn("w-9 h-9 rounded-full border text-sm", trustPrior === n ? "border-[#c9a227] bg-[#1a160a] text-[#c9a227]" : "border-[#1f2537]")}>{n}</button>
                      ))}
                    </div>
                    <div className="text-[11px] text-[#6b7280] mt-2">How much do you trust your own read on decisions of this shape today?</div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-1.5">Social hesitation (optional but revealing)</div>
                    <textarea 
                      className="textarea w-full text-sm" 
                      placeholder="I’m hesitating because the bold direction might make me look like I’m showing off to other designers…"
                      value={hesitationNote} 
                      onChange={e => setHesitationNote(e.target.value)} 
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={saveCurrentBearing} disabled={saving} className="btn-primary px-6 py-3 rounded-2xl flex items-center gap-2 disabled:opacity-60">
                  <Save size={17}/> Drop this bearing
                </button>
                <button onClick={quickHesitation} className="btn-ghost px-5 py-3 rounded-2xl flex items-center gap-2">
                  <PauseCircle size={17}/> Capture a hesitation instead
                </button>
              </div>
              <div className="text-[11px] text-[#6b7280]">Everything is private. You can delete any trace later.</div>
            </div>
          </div>
        )}

        {/* LIBRARY */}
        {route === 'library' && (
          <div>
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="section-title">Past bearings</div>
                <div className="text-2xl tracking-tight">What you chose when the pulls were this strong.</div>
              </div>
              <button onClick={() => setRoute('compass')} className="btn-primary px-4 py-2 text-sm rounded-2xl flex items-center gap-2"><Plus size={16}/> New</button>
            </div>

            {decisions.length === 0 && (
              <div className="empty-state p-8 text-center">No decisions yet. The compass is waiting.</div>
            )}

            <div className="grid gap-3">
              {decisions.map(d => {
                const released = !!d.releasedAt
                return (
                  <button key={d.id} onClick={() => selectDecision(d)} className="card p-5 text-left hover:border-[#3a4258] group">
                    <div className="flex justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-medium text-[#e2e8f0] group-hover:text-white pr-6">{d.decisionText}</div>
                        <div className="text-sm text-[#a1a7c0] mt-1 line-clamp-2">{d.privateNorth}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={cn("text-3xl tabular-nums font-semibold", d.declination > 48 ? "declination-high" : "declination-low")}>{d.declination.toFixed(0)}°</div>
                        <div className="text-[10px] text-[#6b7280]">{released ? 'released' : 'not yet released'}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-[#6b7280] flex gap-3">
                      <span>{formatDistanceToNow(new Date(d.createdAt), { addSuffix: true })}</span>
                      {d.pulls.length > 0 && <span>{d.pulls.length} pulls</span>}
                      {d.hesitationNote && <span className="text-[#c17a3f]">hesitation noted</span>}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* SINGLE DECISION + CURVE */}
        {route === 'curve' && selectedDecision && (
          <div className="max-w-3xl">
            <button onClick={() => { setSelectedId(null); setRoute('library') }} className="text-xs flex items-center gap-1 text-[#6b7280] mb-3 hover:text-[#a1a7c0]">
              <ArrowRight className="rotate-180" size={14}/> Back to library
            </button>

            <div className="mb-5">
              <div className="font-medium text-2xl tracking-tight pr-12">{selectedDecision.decisionText}</div>
              <div className="text-[#a1a7c0] mt-1">{selectedDecision.privateNorth}</div>
              <div className="mt-2 flex items-center gap-2">
                <span className={cn("text-2xl tabular-nums font-semibold", selectedDecision.declination > 48 ? "declination-high" : "declination-low")}>{selectedDecision.declination.toFixed(0)}°</span>
                <span className="text-xs text-[#6b7280]">declination at decision</span>
                {!selectedDecision.releasedAt && (
                  <button onClick={markReleased} className="ml-3 btn-warm text-xs px-4 py-1 rounded-full">Mark as released → start patience curve</button>
                )}
              </div>
            </div>

            <PatienceCurve 
              decision={selectedDecision} 
              signals={currentSignals} 
              onAddSignal={addSignalToSelected} 
            />

            <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-3 card p-5">
                <div className="flex items-center justify-between">
                  <div className="section-title">Provenance pulls at the time</div>
                  <button onClick={loadSimilar} className="text-xs btn-ghost px-3 py-1">Find similar past bearings</button>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  {selectedDecision.pulls.length === 0 && <div className="text-[#6b7280]">No pulls were logged for this decision.</div>}
                  {selectedDecision.pulls.map(p => (
                    <div key={p.id} className="flex gap-3 border-l-2 pl-3" style={{ borderColor: CUE_IS_STATUS[p.cueCategory] ? '#c17a3f' : '#4a6359' }}>
                      <div className="w-8 text-right tabular-nums text-[#6b7280]">{p.strength}</div>
                      <div>
                        <div>{CUE_LABELS[p.cueCategory]}</div>
                        {p.cueDetail && <div className="text-[#a1a7c0] text-xs mt-px">{p.cueDetail}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 space-y-3">
                <button onClick={freezeCurve} className="w-full btn-gold px-5 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm">
                  <Camera size={16}/> Freeze this patience curve as artifact
                </button>
                <button onClick={loadSeedsForSelected} className="w-full btn-ghost px-5 py-3 rounded-2xl flex items-center justify-center gap-2 text-sm">
                  <BookOpen size={16}/> Surface angles to examine
                </button>
                {!selectedDecision.releasedAt && <div className="text-xs text-[#6b7280] px-1">Release the decision to unlock the full patience curve and calibration tracking.</div>}
              </div>
            </div>

            {/* Seeds */}
            {seeds.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-2">Angles others have found useful to examine (not about you)</div>
                <div className="space-y-2">
                  {seeds.map((s, i) => (
                    <div key={i} className="card p-4 text-sm border-l-2 border-[#4a6359]">{s}</div>
                  ))}
                </div>
                <div className="text-[10px] text-[#6b7280] mt-2 px-1">These are starting questions generated from the provenance you logged. You are the only authority on what they mean.</div>
              </div>
            )}

            {similar.length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#6b7280] mb-2">Similar provenance patterns from your past</div>
                {similar.map((s: any) => (
                  <div key={s.id} className="text-sm py-1.5 border-b border-[#1f2537] flex justify-between">
                    <div>{s.decision_text?.slice(0, 90)}</div>
                    <div className="text-[#6b7280] tabular-nums">{s.declination?.toFixed(0)}°</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CALIBRATION */}
        {route === 'calibration' && (
          <div className="max-w-2xl">
            <div className="section-title mb-1">Your calibration</div>
            <div className="text-2xl tracking-tight mb-4">What your status pulls have actually predicted so far.</div>

            {!calibration && <div className="text-[#6b7280]">Loading patterns…</div>}

            {calibration && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="card p-4"><div className="text-4xl font-semibold tabular-nums">{calibration.totalDecisions}</div><div className="text-xs text-[#6b7280] mt-1">decisions logged</div></div>
                  <div className="card p-4"><div className="text-4xl font-semibold tabular-nums">{calibration.avgDeclination}°</div><div className="text-xs text-[#6b7280] mt-1">average declination</div></div>
                  <div className="card p-4"><div className="text-4xl font-semibold tabular-nums">{calibration.highStatusPullDecisions}</div><div className="text-xs text-[#6b7280] mt-1">high status-pull decisions</div></div>
                </div>

                <div className="card p-6">
                  <div className="section-title mb-3">Factual statements from your data</div>
                  <ul className="space-y-3 text-[15px]">
                    {(calibration.factualStatements || []).map((s: string, i: number) => (
                      <li key={i} className="pl-1">• {s}</li>
                    ))}
                  </ul>
                  <div className="text-[11px] text-[#6b7280] mt-6">These statements are computed only from the decisions, pulls, releases, and signals you have entered. They contain no interpretation of your character or “growth”.</div>
                </div>
              </>
            )}

            <div className="mt-8 text-xs text-[#6b7280]">The goal is noticing, not improvement theater. Over many decisions the curve becomes legible.</div>
          </div>
        )}

        {/* HESITATIONS */}
        {route === 'hesitations' && (
          <div className="max-w-2xl">
            <div className="section-title mb-1">Social hesitation log</div>
            <div className="text-2xl tracking-tight mb-4">The moments right before you almost chose the version that photographs better.</div>

            <button onClick={quickHesitation} className="btn-warm mb-5 px-5 py-2 rounded-2xl text-sm">Capture a hesitation now</button>

            {hesitations.length === 0 && <div className="empty-state p-8">No hesitations captured yet. They are the most honest data you have.</div>}

            <div className="space-y-3">
              {hesitations.map(h => (
                <div key={h.id} className="hesitation card p-4 text-sm">
                  {h.text}
                  <div className="text-[10px] text-[#6b7280] mt-2">{format(new Date(h.capturedAt), 'dd MMM yyyy')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIVACY + EXPORT */}
        {route === 'privacy' && (
          <div className="max-w-xl">
            <div className="section-title">Privacy &amp; control</div>
            <div className="text-2xl tracking-tight mt-1 mb-6">Everything here is yours. Delete it whenever you want.</div>

            <div className="space-y-4">
              <button onClick={doExport} className="btn-ghost w-full justify-start px-5 py-4 rounded-2xl flex items-center gap-3">
                <Download size={18}/> Export everything as JSON
              </button>
              <button onClick={deleteEverything} className="btn-danger w-full justify-start px-5 py-4 rounded-2xl flex items-center gap-3">
                <Trash2 size={18}/> Delete every decision, signal, and hesitation forever
              </button>
            </div>

            <div className="mt-8 text-xs text-[#6b7280] leading-relaxed">
              Declination stores decision text, the exact cue language you wrote, pull strengths and angles, signals you log later, and hesitation notes. 
              Nothing is shared unless you explicitly freeze a curve and opt it into an anonymous public gallery (not yet implemented in this build).
              The creator links below are here because the person who built this believes tools like this should be made in public by named humans.
            </div>

            <div className="mt-6"><CreatorLinks variant="footer" /></div>
          </div>
        )}
      </div>

      {/* Bottom persistent creator + privacy links */}
      <div className="max-w-5xl mx-auto px-5 mt-16 pt-6 border-t border-[#1f2537] text-xs flex flex-wrap gap-x-6 gap-y-2 items-center text-[#6b7280]">
        <CreatorLinks variant="footer" />
        <button onClick={() => setRoute('privacy')} className="hover:text-[#a1a7c0] underline-offset-2 hover:underline">Privacy &amp; deletion</button>
        <span className="text-[#2a3146]">•</span>
        <span>Works offline. Data lives in your compass first.</span>
      </div>

      {/* Command palette */}
      <AnimatePresence>
        {showCmd && (
          <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[18vh] bg-black/70" onClick={() => setShowCmd(false)}>
            <div className="card w-full max-w-md p-2" onClick={e => e.stopPropagation()}>
              {[
                { label: 'New bearing', action: () => { setRoute('compass'); setShowCmd(false) } },
                { label: 'Library of decisions', action: () => { setRoute('library'); setShowCmd(false) } },
                { label: 'Capture hesitation', action: () => { setShowCmd(false); quickHesitation() } },
                { label: 'View calibration', action: () => { setRoute('calibration'); setShowCmd(false) } },
                { label: 'Export my data', action: () => { setShowCmd(false); doExport() } },
                { label: 'Privacy & deletion', action: () => { setRoute('privacy'); setShowCmd(false) } },
              ].map((item, idx) => (
                <button key={idx} onClick={item.action} className="w-full text-left px-4 py-3 hover:bg-[#1a1f2b] rounded-xl flex items-center gap-3 text-sm">
                  {item.label}
                </button>
              ))}
              <div className="px-4 pt-2 pb-1 text-[10px] text-[#6b7280]">LinkedIn &amp; Buy me a coffee are always one click away in the footer.</div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
